import { create } from 'zustand';
import { EntityName } from 'shared/domain/entityName';
import type { IssueWorkflowOutDto } from 'shared/domain/workflow/types';
import { workflowClient } from 'shared/domain/workflow/workflowClient';
import { createSelectors, StoreName, withDevtools } from '../config';
import { createEntityCollectionActions } from '../entityCollection';
import { createDataActions, createInitialActionsState, createInitialData, makeAction, type ZustandSet } from '../types';
import { addWorkflow, removeStage, replaceStage, replaceWorkflow, upsertStage } from './model';
import type { WorkflowStore } from './types';
import { workflowActionNames } from './types';

function updateWorkflows(
  set: ZustandSet<WorkflowStore>,
  update: (workflows: IssueWorkflowOutDto[]) => IssueWorkflowOutDto[]
): void {
  set((state) => ({ ...state, data: { ...state.data, items: update(state.data.items) } }));
}

const useWorkflowStoreBase = create<WorkflowStore>()(
  withDevtools(
    (set, get) => ({
      data: createInitialData<IssueWorkflowOutDto>(),
      _actionStates: createInitialActionsState(workflowActionNames),

      actions: {
        ...createDataActions<IssueWorkflowOutDto, WorkflowStore>(set),
        ...createEntityCollectionActions(
          { client: workflowClient, entityName: EntityName.issueWorkflows },
          () => get().actions
        ),
        reset: () =>
          set((state) => ({
            ...state,
            data: { items: [], loading: true },
            _actionStates: createInitialActionsState(workflowActionNames),
          })),
      },

      // Each change applies what the server saved at once; the worker stores it too.
      asyncActions: {
        createWorkflow: makeAction(set, 'createWorkflow', async (set, dto, projectId) => {
          const created = await workflowClient.createWorkflow(dto, projectId);
          updateWorkflows(set, (workflows) => addWorkflow(workflows, created));
          return created;
        }),

        updateWorkflow: makeAction(set, 'updateWorkflow', async (set, workflowId, dto, projectId) => {
          const saved = await workflowClient.editWorkflow(workflowId, dto, projectId);
          updateWorkflows(set, (workflows) => replaceWorkflow(workflows, workflowId, saved));
          return saved;
        }),

        createStage: makeAction(set, 'createStage', async (set, workflowId, dto, projectId) => {
          const created = await workflowClient.createStage(workflowId, dto, projectId);
          updateWorkflows(set, (workflows) => upsertStage(workflows, workflowId, created));
          return created;
        }),

        updateStage: makeAction(set, 'updateStage', async (set, workflowId, stageId, dto, projectId) => {
          const saved = await workflowClient.editStage(workflowId, stageId, dto, projectId);
          updateWorkflows(set, (workflows) => replaceStage(workflows, workflowId, stageId, saved));
          return saved;
        }),

        deleteStage: makeAction(set, 'deleteStage', async (set, workflowId, stageId, projectId) => {
          const deleted = await workflowClient.deleteStage(workflowId, stageId, projectId);
          updateWorkflows(set, (workflows) => removeStage(workflows, workflowId, stageId));
          return deleted;
        }),

        reorderStage: makeAction(set, 'reorderStage', async (set, workflowId, stageId, dto, projectId) => {
          const saved = await workflowClient.reorderStage(workflowId, stageId, dto, projectId);
          updateWorkflows(set, (workflows) => replaceStage(workflows, workflowId, stageId, saved));
          return saved;
        }),

        createDecision: makeAction(set, 'createDecision', (_set, projectId, issueId, dto) =>
          workflowClient.createDecision(projectId, issueId, dto)
        ),
      },
    }),
    StoreName.Workflows
  )
);

export const useWorkflowStore = createSelectors(useWorkflowStoreBase);

export const getWorkflowStoreState = (): WorkflowStore =>
  useWorkflowStore.getState();
